import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock } from 'lucide-react';

interface MiniGameWrapperProps {
  title: string;
  instructions: string;
  timeLeft: number;
  maxTime: number;
  onExit: () => void;
  children: React.ReactNode;
}

const MiniGameWrapper: React.FC<MiniGameWrapperProps> = ({
  title,
  instructions,
  timeLeft,
  maxTime,
  onExit,
  children,
}) => {
  const timePercentage = (timeLeft / maxTime) * 100;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-br from-purple-900/95 to-blue-900/95 border-2 border-purple-500/50 rounded-2xl p-6 max-w-3xl w-full mx-4 shadow-2xl"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">{title}</h2>
            <p className="text-sm text-purple-200 mt-1">{instructions}</p>
          </div>
          <button
            onClick={onExit}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Timer Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <div className="flex items-center gap-2 text-white">
              <Clock className="h-4 w-4" />
              <span>Time Remaining</span>
            </div>
            <span className={`font-bold ${timePercentage < 30 ? 'text-red-400' : 'text-white'}`}>
              {timeLeft}s
            </span>
          </div>
          <div className="h-2 bg-black/40 rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${
                timePercentage < 30 ? 'bg-red-500' : timePercentage < 60 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              initial={{ width: '100%' }}
              animate={{ width: `${timePercentage}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Game Content */}
        <div className="bg-black/30 rounded-xl p-6 min-h-[300px]">
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MiniGameWrapper;
